import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { showErrorToast, showSuccessToast } from '@/lib/toast-utils';
import { setApiHostname } from '@/lib/apiHostname';

interface AppSettingsContextType {
  settings: Record<string, string>;
  isLoading: boolean;
  updateSetting: (key: string, value: string) => Promise<void>;
  refetch: () => Promise<void>;
}

const AppSettingsContext = createContext<AppSettingsContextType | undefined>(undefined);

export const AppSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('*');

      if (error) throw error;

      const settingsMap: Record<string, string> = {};
      data?.forEach((setting) => {
        settingsMap[setting.setting_key] = setting.setting_value;
      });
      setApiHostname(settingsMap.api_hostname);
      setSettings(settingsMap);
    } catch (error) {
      console.error('Error fetching app settings:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateSetting = useCallback(async (key: string, value: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('update-app-setting', {
        body: { key, value }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (key === 'api_hostname') setApiHostname(value);
      setSettings(prev => ({ ...prev, [key]: value }));
      showSuccessToast(`${key} has been updated`, 'Setting updated');
    } catch (error) {
      console.error('Error updating setting:', error);
      showErrorToast('Failed to update setting');
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return (
    <AppSettingsContext.Provider value={{ settings, isLoading, updateSetting, refetch: fetchSettings }}>
      {children}
    </AppSettingsContext.Provider>
  );
};

export const useAppSettings = () => {
  const context = useContext(AppSettingsContext);
  if (!context) {
    throw new Error('useAppSettings must be used within an AppSettingsProvider');
  }
  return context;
};
